
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { isPwaMode } from "@/utils/pwaUtils";

interface NavigationItem {
  path: string;
  icon: React.ElementType;
  label: string;
}

interface BottomNavigationProps {
  navItems: NavigationItem[];
  isActive: (path: string) => boolean;
  onMenuClick: () => void;
}

export function BottomNavigation({ navItems, isActive, onMenuClick }: BottomNavigationProps) {
  const navigate = useNavigate();
  const isPwa = isPwaMode();

  const calculateIconSize = (width: number): number => {
    // Scale icon size between 24px and 48px based on screen width
    // Uses a divisor of 12, meaning for every 12px of screen width, icon size changes by 1px (approx)
    // Min size 24px, Max size 48px
    return Math.max(24, Math.min(48, Math.floor(width / 12)));
  };

  const [iconSize, setIconSize] = useState(() => 
    typeof window !== 'undefined' ? calculateIconSize(window.innerWidth) : 32 // Default to 32 if window undefined initially
  );

  useEffect(() => {
    const handleResize = () => {
      setIconSize(calculateIconSize(window.innerWidth));
    };

    // Ensure initial size is set correctly after mount if window was undefined
    if (typeof window !== 'undefined') {
      handleResize();
    }
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 bg-background border-t flex justify-between items-start z-30 shadow-lg",
      isPwa ? "pwa-bottom-navigation" : "h-16"
    )}>
      {navItems.map((item) => (
        <button
          key={item.path}
          className={cn(
            "flex flex-col items-center justify-center flex-1 text-sm",
            isActive(item.path) 
              ? "text-primary" 
              : "text-muted-foreground"
          )}
          onClick={() => navigate(item.path)}
        >
          <item.icon size={iconSize} />
          <span className="text-xs font-medium">{item.label}</span>
        </button>
      ))}
      <button
        className="flex flex-col items-center justify-center flex-1 text-sm"
        onClick={onMenuClick}
      >
        <Menu size={iconSize} />
        <span className="text-xs font-medium">Menu</span>
      </button>
    </div>
  );
}
